/* Soundium spec-value glossary (SND-16780).
 *
 * The Specs & Compare app renders its table inside the OPEN shadow root of
 * <sc-comparison-table>; the tech sheet renders a plain .stsh__table in the light
 * DOM. This file walks both, finds VALUE cells whose text matches a glossary
 * value or alias, and wraps that text in a <button class="snd-gl"> that opens one
 * shared popover (appended to <body>, position: fixed — a popover built inside the
 * shadow root would be clipped by the table's own scroller).
 *
 * Data comes from snippets/snd-spec-glossary.liquid as
 * <script type="application/json" id="snd-gl-data"> (already reduced to the
 * current locale). Keys are short on purpose: a=attribute, v=value, s=aliases,
 * x=explanation, n=rank note, r=related values.
 */
(function () {
  'use strict';
  var DATA_ID = 'snd-gl-data';
  var node = document.getElementById(DATA_ID);
  if (!node) return;
  var ENTRIES;
  try { ENTRIES = JSON.parse(node.textContent || '[]'); } catch (e) { return; }
  if (!ENTRIES.length) return;

  var LABELS = (function () {
    try { return JSON.parse(node.getAttribute('data-labels') || '{}'); } catch (e) { return {}; }
  })();

  /* ---------- text normalisation ------------------------------------------ */
  function norm(s) {
    return (s || '')
      .replace(/[‘’‚‛′]/g, "'")
      .replace(/[“”„‟″]/g, '"')
      .replace(/[‐-―−]/g, '-')
      .replace(/ /g, ' ')
      .normalize('NFD').replace(/[̀-ͯ]/g, '')
      .toLowerCase()
      .replace(/\s+/g, ' ')
      .replace(/[.,;:!?]+$/, '')
      .trim();
  }

  /* value text -> [entry, ...] (several attributes can share a word) */
  var INDEX = Object.create(null);
  var BY_VALUE = Object.create(null);
  function put(key, entry) {
    var k = norm(key);
    if (!k || k.length < 2) return;
    (INDEX[k] || (INDEX[k] = [])).push(entry);
  }
  ENTRIES.forEach(function (e) {
    e._attr = [];
    put(e.v, e);
    (e.s || []).forEach(function (a) {
      if (a.indexOf('attr:') === 0) e._attr.push(norm(a.slice(5)));
      else if (a.indexOf('lt:') === 0) e._lt = a.slice(3);
      else put(a, e);
    });
    BY_VALUE[e.v] = e;
  });

  /* When a word means different things in different rows ("USB" as a connection
     and as a power source), the row LABEL decides. */
  function pick(list, label) {
    if (list.length === 1) return list[0];
    var l = norm(label);
    for (var i = 0; i < list.length; i++) {
      var attrs = list[i]._attr;
      for (var j = 0; j < attrs.length; j++) {
        if (attrs[j] && (l === attrs[j] || l.indexOf(attrs[j]) === 0)) return list[i];
      }
    }
    for (var i2 = 0; i2 < list.length; i2++) {
      var a2 = list[i2]._attr;
      for (var j2 = 0; j2 < a2.length; j2++) if (a2[j2] && l.indexOf(a2[j2]) !== -1) return list[i2];
    }
    return list[0];
  }

  /* ---------- splitting a cell into candidate values ---------------------- */
  /* "MIDI ir Nesiojamas", "6.3 mm jack ir 3.5 mm jack", "RCA, XLR, Optical" */
  /* SND-17477: ", and X" (Oxford comma) used to leave "and X" as one part, so the
     last value of a 3+ item list was never tagged — the conjunction is now
     swallowed together with the comma. */
  var CONJ = '(?:ir|and|und|et|oraz|y|e|en|och|og|ja|vai|un)';
  var SPLIT = new RegExp('\\s*(?:,|;|·|，|\\s\\/\\s)\\s*(?:' + CONJ + '\\s+)?|\\s' + CONJ + '\\s', 'i');

  var ICON =
    '<svg class="snd-gl__i" viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor"' +
    ' stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false">' +
    '<circle cx="12" cy="12" r="10"/><path d="M12 16V12"/>' +
    '<path d="M12.125 8.25H12M12.25 8.25C12.25 8.11193 12.1381 8 12 8C11.8619 8 11.75 8.11193 11.75 8.25C11.75 8.38807 11.8619 8.5 12 8.5C12.1381 8.5 12.25 8.38807 12.25 8.25Z"/></svg>';

  /* Styles for the button itself must live in every root that holds one. */
  var BTN_CSS =
    '.snd-gl{display:inline;font:inherit;color:inherit;background:none;border:0;padding:0;margin:0;' +
    'cursor:help;text-align:inherit;border-bottom:1px dotted rgba(0,0,0,.35)}' +
    '.snd-gl:hover{border-bottom-color:#0352f2}' +
    '.snd-gl:focus-visible{outline:2px solid #0352f2;outline-offset:2px;border-radius:3px}' +
    '.snd-gl__i{display:inline-block;vertical-align:-2px;margin-inline-start:4px;opacity:.5}' +
    '.snd-gl:hover .snd-gl__i,.snd-gl[aria-expanded="true"] .snd-gl__i{opacity:1;color:#0352f2}' +
    '@media (prefers-reduced-motion:no-preference){.snd-gl__i{transition:opacity .15s ease,color .15s ease}}' +
    /* SND-17477: the app's value cell is display:flex — every text fragment and
       button we produce would become its own flex item (edge spaces dropped,
       words broken by overflow-wrap:anywhere). One inline wrapper = one item. */
    '.snd-gl-run{display:inline;min-width:0;max-width:100%}' +
    '.snd-gl__w{white-space:nowrap}';

  var styled = typeof WeakSet === 'function' ? new WeakSet() : null;
  function ensureStyle(root) {
    if (!root) return;
    if (styled) { if (styled.has(root)) return; styled.add(root); }
    else if (root.querySelector('style[data-snd-gl]')) return;
    var st = document.createElement('style');
    st.setAttribute('data-snd-gl', '');
    st.textContent = BTN_CSS;
    (root.head || root).appendChild(st);
  }

  /* ---------- the shared popover ------------------------------------------ */
  var pop, popTitle, popBody, popRank, popRel, current = null, hideTimer = null;
  function buildPop() {
    if (pop) return pop;
    pop = document.createElement('div');
    pop.className = 'snd-glpop';
    pop.setAttribute('role', 'dialog');
    pop.setAttribute('aria-modal', 'false');
    pop.hidden = true;
    pop.innerHTML =
      '<div class="snd-glpop__panel">' +
      '<p class="snd-glpop__title"></p>' +
      '<p class="snd-glpop__body"></p>' +
      '<p class="snd-glpop__rank"></p>' +
      '<div class="snd-glpop__rel"></div>' +
      '</div>';
    popTitle = pop.querySelector('.snd-glpop__title');
    popBody = pop.querySelector('.snd-glpop__body');
    popRank = pop.querySelector('.snd-glpop__rank');
    popRel = pop.querySelector('.snd-glpop__rel');
    pop.addEventListener('mouseenter', function () { clearTimeout(hideTimer); });
    pop.addEventListener('mouseleave', function () { scheduleHide(); });
    document.body.appendChild(pop);
    return pop;
  }

  function place(btn) {
    var r = btn.getBoundingClientRect();
    var w = Math.min(320, window.innerWidth - 24);
    pop.style.width = w + 'px';
    var left = Math.min(Math.max(12, r.left), window.innerWidth - w - 12);
    pop.style.left = Math.round(left) + 'px';
    var h = pop.offsetHeight;
    var below = window.innerHeight - r.bottom;
    var top = below > h + 16 || r.top < h + 16 ? r.bottom + 8 : r.top - h - 8;
    pop.style.top = Math.round(Math.max(8, top)) + 'px';
  }

  function show(btn) {
    var e = btn.__sndgl;
    if (!e) return;
    clearTimeout(hideTimer);
    buildPop();
    popTitle.textContent = btn.__sndglLabel || e.v;
    popBody.textContent = e.x || '';
    popRank.textContent = e.n || '';
    popRank.hidden = !e.n;
    popRel.textContent = '';
    var rel = e.r || [];
    if (rel.length) {
      var lead = document.createElement('span');
      lead.className = 'snd-glpop__rello';
      lead.textContent = (LABELS.related || 'Related') + ':';
      popRel.appendChild(lead);
      rel.slice(0, 4).forEach(function (r) {
        var chip = document.createElement('span');
        chip.className = 'snd-glpop__chip';
        /* show the alternative the way the shop shows it in this locale */
        var other = BY_VALUE[r];
        chip.textContent = (LABELS.lang === 'lt' && other && other._lt) ? other._lt : r;
        popRel.appendChild(chip);
      });
    }
    popRel.hidden = !rel.length;
    pop.hidden = false;
    place(btn);
    if (current && current !== btn) current.setAttribute('aria-expanded', 'false');
    current = btn;
    btn.setAttribute('aria-expanded', 'true');
  }

  function hide() {
    if (!pop || pop.hidden) return;
    pop.hidden = true;
    if (current) { current.setAttribute('aria-expanded', 'false'); current = null; }
  }
  function scheduleHide() { clearTimeout(hideTimer); hideTimer = setTimeout(hide, 160); }

  document.addEventListener('keydown', function (ev) {
    if (ev.key === 'Escape' && current) { var c = current; hide(); try { c.focus(); } catch (e) {} }
  });
  document.addEventListener('click', function (ev) {
    if (!current || !pop || pop.hidden) return;
    if (pop.contains(ev.target)) return;
    var path = ev.composedPath ? ev.composedPath() : [];
    if (path.indexOf(current) !== -1) return;
    hide();
  }, true);
  window.addEventListener('scroll', function () { if (current) place(current); }, true);
  window.addEventListener('resize', function () { if (current) place(current); });

  /* ---------- wrapping the matched text ----------------------------------- */
  function makeButton(text, entry) {
    var b = document.createElement('button');
    b.type = 'button';
    b.className = 'snd-gl';
    b.setAttribute('aria-expanded', 'false');
    b.setAttribute('aria-haspopup', 'dialog');
    b.setAttribute('title', entry.x || '');
    /* last word + icon never separate: "Detachable Cable" may wrap, "Cable ⓘ" not */
    var sp = text.lastIndexOf(' ');
    var lastWord = document.createElement('span');
    lastWord.className = 'snd-gl__w';
    if (sp > 0) b.appendChild(document.createTextNode(text.slice(0, sp + 1)));
    lastWord.appendChild(document.createTextNode(sp > 0 ? text.slice(sp + 1) : text));
    lastWord.insertAdjacentHTML('beforeend', ICON);
    b.appendChild(lastWord);
    b.__sndgl = entry;
    b.__sndglLabel = text;
    b.addEventListener('click', function (ev) {
      ev.preventDefault(); ev.stopPropagation();
      if (current === b && pop && !pop.hidden) hide(); else show(b);
    });
    b.addEventListener('mouseenter', function () {
      if (window.matchMedia && window.matchMedia('(hover: hover)').matches) show(b);
    });
    b.addEventListener('mouseleave', scheduleHide);
    b.addEventListener('focus', function () { show(b); });
    return b;
  }

  /* Per-character normalisation. It deliberately does NOT strip trailing
     punctuation the way norm() does: applied character by character that rule
     would delete every "." and turn "3.5 mm jack" into "35 mm jack".*/
  function normChar(ch) {
    return ch
      .replace(/[‘’‚‛′]/g, "'")
      .replace(/[“”„‟″]/g, '"')
      .replace(/[‐-―−]/g, '-')
      .replace(/[ \s]/g, ' ')
      .normalize('NFD').replace(/[̀-ͯ]/g, '')
      .toLowerCase();
  }

  /* A normalised copy of a string plus, for every normalised character, the index
     it came from in the original. That is what lets us wrap the EXACT raw run
     ("6.3 mm jack") after matching on the normalised one. */
  function normMap(raw) {
    var out = '', idx = [], prevSpace = true;
    for (var p = 0; p < raw.length; p++) {
      var piece = normChar(raw[p]);
      if (!piece) continue;
      if (piece === ' ') {
        if (prevSpace) continue;
        prevSpace = true;
      } else prevSpace = false;
      for (var q = 0; q < piece.length; q++) { out += piece[q]; idx.push(p); }
    }
    return { s: out, idx: idx };
  }

  /* Replace one plain-text run inside a cell with <button>. Works on text nodes
     so the app's own markup (links, badges) is left untouched. */
  function wrapInCell(cell, text, entry) {
    var walker = document.createTreeWalker(cell, NodeFilter.SHOW_TEXT, null, false);
    /* match against the same normalisation the haystack uses */
    var target = normMap(text).s.trim(), n;
    if (!target) return false;
    while ((n = walker.nextNode())) {
      if (!n.nodeValue || !n.nodeValue.trim()) continue;
      if (n.parentNode && n.parentNode.closest && n.parentNode.closest('.snd-gl')) continue;
      var m = normMap(n.nodeValue);
      var i = m.s.indexOf(target);
      if (i === -1) continue;
      /* whole words only: no letter or digit may touch either end */
      var before = m.s.charAt(i - 1), after = m.s.charAt(i + target.length);
      if (/[a-z0-9]/.test(before) || /[a-z0-9]/.test(after)) continue;
      var start = m.idx[i];
      var end = m.idx[i + target.length - 1] + 1;
      if (start === undefined || end === undefined || end <= start) continue;
      var tail = n.splitText(start);
      tail.splitText(end - start);
      var btn = makeButton(tail.nodeValue, entry);
      tail.parentNode.replaceChild(btn, tail);
      return true;
    }
    return false;
  }

  /* The app fills its cells progressively, so "already handled" is remembered per
     TEXT, not per cell: a cell that was empty (or held another product) on the
     first pass is looked at again once its text changes. */
  function sig(t) { return t.length + ':' + t.slice(0, 24); }

  function handleCell(cell, label) {
    if (!cell) return 0;
    var text = (cell.textContent || '').trim();
    if (!text || text.length > 160 || text === '\u2014') return 0;
    /* The app also re-renders a cell with the SAME text (locale/price refresh),
       which drops our button — so a remembered signature only counts while the
       button it produced is still in place. */
    if (cell.getAttribute('data-sndgl') === sig(text) &&
        (cell.getAttribute('data-sndgl-hit') !== '1' || cell.querySelector('.snd-gl'))) return 0;
    var hits = 0;
    var whole = INDEX[norm(text)];
    if (whole) {
      if (wrapInCell(cell, text, pick(whole, label))) hits++;
    } else {
      var parts = text.split(SPLIT);
      for (var i = 0; i < parts.length && i < 8; i++) {
        var p = parts[i].trim();
        if (!p) continue;
        var list = INDEX[norm(p)];
        if (list && wrapInCell(cell, p, pick(list, label))) hits++;
      }
    }
    if (hits) wrapRun(cell);
    cell.setAttribute('data-sndgl', sig(text));
    cell.setAttribute('data-sndgl-hit', hits ? '1' : '0');
    return hits;
  }

  /* SND-17477: after wrapping, a cell holds [text, button, text, button, ...].
     Inside a display:flex cell (the Specs & Compare app) each of those would be a
     separate flex item: the " and " / ", " fragments lose their edge spaces and
     the items shrink until words break mid-word. Moving everything into ONE
     inline span restores normal inline flow whatever the cell's display is. */
  function wrapRun(cell) {
    var kids = cell.childNodes;
    if (kids.length === 1 && kids[0].nodeType === 1 && kids[0].classList &&
        kids[0].classList.contains('snd-gl-run')) return;
    var run = document.createElement('span');
    run.className = 'snd-gl-run';
    while (cell.firstChild) run.appendChild(cell.firstChild);
    cell.appendChild(run);
  }

  var count = 0;
  function scanRoot(root) {
    if (!root || !root.querySelectorAll) return;
    var did = false;
    /* Specs & Compare rows */
    root.querySelectorAll('.row--attribute').forEach(function (row) {
      var nameEl = row.querySelector('.attribute-name');
      var label = nameEl ? (nameEl.childNodes[0] ? nameEl.childNodes[0].textContent : nameEl.textContent) : '';
      row.querySelectorAll('.attribute-value').forEach(function (cell) {
        count += handleCell(cell, label); did = true;
      });
    });
    /* Tech sheet + any Soundium-built spec table */
    root.querySelectorAll('.stsh__table tr, .snd-spec-row').forEach(function (tr) {
      var th = tr.querySelector('th');
      var label = th ? th.textContent : '';
      tr.querySelectorAll('td').forEach(function (cell) { count += handleCell(cell, label); did = true; });
    });
    return did;
  }

  var roots = [];
  function collectRoots() {
    roots = [document];
    document.querySelectorAll('sc-comparison-table, sc-dynamic-compare, sc-dynamic-compare-dialog').forEach(function (h) {
      if (h.shadowRoot) { roots.push(h.shadowRoot); ensureStyle(h.shadowRoot); }
    });
    ensureStyle(document.head ? document : null);
  }

  var pending = null;
  function run() {
    collectRoots();
    roots.forEach(scanRoot);
    document.documentElement.setAttribute('data-snd-gl-count', String(count));
  }
  function schedule() {
    if (pending) return;
    pending = setTimeout(function () { pending = null; run(); }, 150);
  }

  var mo = new MutationObserver(schedule);
  function observeAll() {
    try { mo.observe(document.body, { childList: true, subtree: true }); } catch (e) {}
    document.querySelectorAll('sc-comparison-table, sc-dynamic-compare, sc-dynamic-compare-dialog').forEach(function (h) {
      if (h.shadowRoot && !h.__sndglObs) { h.__sndglObs = 1; mo.observe(h.shadowRoot, { childList: true, subtree: true }); }
    });
  }

  function boot() { run(); observeAll(); setTimeout(function () { observeAll(); run(); }, 1200); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
  window.addEventListener('load', function () { observeAll(); schedule(); });
  document.addEventListener('shopify:section:load', schedule);
})();

